// New and edit job.
import {
  addDays,
  type JobBilling as BillingKind,
  firstRepeat,
  FREQUENCIES,
  type HomeAnswers,
  type JobDetail,
  moveRepeat,
  nextAfterSwitch,
  type QuickQuote,
  type QuoteDetail,
  type RepeatValue,
} from "@steward/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type SubmitEvent, useState } from "react";
import { useChecklists, useClient, useClients, useCreateJob, useJob, usePeople, useUpdateJob } from "../../api/hooks";
import { useQuote } from "../../api/money";
import { useSession } from "../../app/session";
import { Choice, Combobox, FormField, InputText } from "../../components/fields";
import { phoneText, shortDate, todayIn } from "../../components/format";
import { Icon } from "../../components/Icon";
import { markFresh } from "../../components/next";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, LoadError, Page, Skeleton, Switch } from "../../components/parts";
import { RepeatPicker } from "../../components/repeat";
import { draftLine, type DraftLine, errText, LinesEditor, linesOut } from "../../components/work";
import { LawnQuickPrice, QuickPrice } from "../QuickPrice";
import { NEW_CLIENT, NewClientBox, type NewClientStart } from "../quotes/Form";

export function JobForm({ id, clientId, quoteId }: { id?: string; clientId?: string; quoteId?: string }) {
  const existing = useJob(id ?? "");
  const quote = useQuote(quoteId ?? "");
  if (id && !existing.data)
    return <Page title="Edit job">{existing.error ? <LoadError error={existing.error} /> : <Skeleton rows={6} />}</Page>;
  if (quoteId && !quote.data)
    return <Page title="New job">{quote.error ? <LoadError error={quote.error} /> : <Skeleton rows={6} />}</Page>;
  if (quote.data && quote.data.status !== "approved")
    return (
      <Page title="New job" subtitle={`Quote #${String(quote.data.number)}`}>
        <Banner type="warning">
          {quote.data.status === "converted"
            ? "This quote is already a job."
            : "Only an approved quote can become a job. The client approves it first."}
        </Banner>
      </Page>
    );
  return <JobFormBody j={id ? existing.data : undefined} clientId={clientId} q={quote.data} />;
}

/** Make into a job: the quote's approved lines, picked extras included. A repeat's first visit only lines stay on the first visit. */
function quoteLines(q: QuoteDetail, repeats: boolean): DraftLine[] {
  return q.lines
    .filter((l) => !l.optional || l.picked)
    .map((l) =>
      draftLine({
        description: l.description,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        note: l.note,
        link: l.link ?? null,
        frequency: repeats && !l.recurring ? "once" : "every",
      }),
    );
}

/**
 * New job and Edit job, in the order a call goes (owner, 2026-10-30: "like a quote with fast adding"): who it is for, the
 * price (Price it fast fills the lines), the work and how often, when, the crew, the office note. A new client confirms
 * first: they get a link to save a card and confirm, and the job goes on the schedule once they do (api jobs/confirm.ts).
 */
function JobFormBody({
  j,
  clientId,
  q,
}: {
  j: JobDetail | undefined;
  clientId: string | undefined;
  /** Make into a job on a quote: the form opens filled from it, and saving marks the quote a job. */
  q?: QuoteDetail | undefined;
}) {
  const { company } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const create = useCreateJob();
  const update = useUpdateJob();
  const today = todayIn(company.timezone);
  const clients = useClients(company.id, { status: "active", sort: "last_activity" }, !j);
  const people = usePeople(company.id);
  const checklists = useChecklists(company.id);
  const [client, setClient] = useState(j?.clientId ?? q?.clientId ?? clientId ?? "");
  const picked = useClient(client);
  const [newClient, setNewClient] = useState<NewClientStart | null>(null);
  const [madeHere, setMadeHere] = useState<string | null>(null);
  const quoteOften = q && (FREQUENCIES as readonly string[]).includes(q.frequency) ? q.frequency : null;
  const [f, setF] = useState({
    title: j?.title ?? q?.title ?? "",
    service: j?.service ?? "",
    startDate: j?.startDate ?? addDays(today, 1),
    startTime: j?.startTime ?? "09:00",
    hours: j?.hours ? String(j.hours) : "",
    endsOn: j?.endsOn ?? "",
    propertyId: j?.propertyId ?? "",
    billing: (j?.billing ?? "visit") as BillingKind,
    officeNotes: j?.officeNotes ?? "",
    crewNotes: j?.crewNotes ?? "",
    crewNotesEs: j?.crewNotesEs ?? "",
    checklistId: j?.checklist?.id ?? "",
  });
  const [repeat, setRepeat] = useState<RepeatValue>(
    j
      ? { frequency: j.frequency, repeatDay: j.repeatDay, repeatRule: j.repeatRule }
      : { frequency: quoteOften ?? "weekly", repeatDay: null, repeatRule: null },
  );
  const [keepsGoing, setKeepsGoing] = useState(!j?.endsOn);
  const [crew, setCrew] = useState<Set<string>>(new Set(j?.crew.map((c) => c.id) ?? []));
  const [lines, setLines] = useState<DraftLine[]>(() => {
    if (j?.lines.length) return j.lines.map(draftLine);
    const fromQuote = q ? quoteLines(q, (quoteOften ?? "weekly") !== "one-time") : [];
    return fromQuote.length ? fromQuote : [draftLine()];
  });
  const [linesTouched, setLinesTouched] = useState(false);
  const [linesFrom, setLinesFrom] = useState(today);
  // The client signed the quote, so no confirm link unless the office turns it on.
  const [confirm, setConfirm] = useState<boolean | null>(q ? false : null);
  const [error, setError] = useState<string | null>(null);
  const repeating = repeat.frequency !== "one-time";
  // Switching how often a started job runs: the new repeat counts from the last visit done (Every 2 weeks to Monthly with
  // the last cut Oct 24 is Nov 21), not the start date. The office can pick another next visit.
  const started = Boolean(j?.startDate && j.startDate < today && f.startDate === j.startDate);
  const was: RepeatValue | null = j ? { frequency: j.frequency, repeatDay: j.repeatDay, repeatRule: j.repeatRule } : null;
  const switching = started && was !== null && repeating && JSON.stringify(repeat) !== JSON.stringify(was);
  const [nextOn, setNextOn] = useState<string | null>(null);
  const pickRepeat = (v: RepeatValue) => {
    if (!started || !j || v.frequency === "one-time") {
      setRepeat(v);
      return;
    }
    const d = nextAfterSwitch(v, j.lastDone, today, was ?? undefined);
    setNextOn(d);
    setRepeat(moveRepeat(v, d));
  };
  // The Repeats list is named from the day the repeat counts from (Weekly on Mondays).
  const repeatDate =
    switching && nextOn
      ? nextOn
      : (firstRepeat({ ...repeat, startDate: f.startDate, repeatFrom: j?.repeatFrom ?? null }) ?? f.startDate);
  const set = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  const team = (people.data ?? []).filter((p) => p.active);
  const who = picked.data?.id === client ? picked.data : undefined;
  // Every client confirms (owner, 2026-10-30: they OK it on the phone, then say they never did). A card on file means sign only.
  // A client who already signed a quote for it does not: they get the usual Job scheduled text.
  const signed = Boolean(who?.signedQuote);
  const askConfirm = !j && Boolean(who) && (confirm ?? !signed);
  const hasCard = Boolean(who?.card) && madeHere !== who?.id;
  const place = who?.properties.find((p) => p.id === f.propertyId) ?? who?.properties[0];
  const size = place ? { beds: place.beds, baths: place.baths, rooms: place.rooms, sqft: place.sqft } : undefined;
  const applyQuick = (quick: QuickQuote, answers: HomeAnswers | null) => {
    const frequency = (FREQUENCIES as readonly string[]).includes(quick.frequency) ? quick.frequency : repeat.frequency;
    const repeats = frequency !== "one-time";
    setF({ ...f, title: quick.title });
    if (frequency !== repeat.frequency) setRepeat({ frequency, repeatDay: null, repeatRule: null });
    // A repeat's first visit lines (the deep clean, the add-ons) go on the first visit only.
    setLines(
      quick.lines.map((l, i) =>
        draftLine({
          description: l.description,
          quantity: 1,
          unitPriceCents: l.unitPriceCents,
          note: l.note ?? null,
          link: l.link ?? null,
          frequency: repeats && !l.recurring ? "once" : "every",
          answers: i === 0 ? answers : null,
        }),
      ),
    );
    setLinesTouched(true);
    setError(null);
    toast("Price is on the job");
  };
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const out = linesOut(lines);
    if (typeof out === "string") {
      setError(out);
      return;
    }
    if (!client && !q) {
      setError("Pick the client.");
      return;
    }
    if (repeating && !keepsGoing && !f.endsOn) {
      setError("Pick the end date, or pick Keeps going.");
      return;
    }
    if (repeating && !keepsGoing && f.endsOn < f.startDate) {
      setError("The end date is before the start.");
      return;
    }
    if (askConfirm && !out.length) {
      setError("Add the price so the client can see what they confirm.");
      return;
    }
    const hours = f.hours.trim() ? Number(f.hours) : null;
    const body = {
      title: f.title.trim() || undefined,
      service: f.service.trim() || undefined,
      frequency: repeat.frequency as (typeof FREQUENCIES)[number],
      startDate: f.startDate,
      repeatDay: repeat.repeatDay,
      repeatRule: repeat.repeatRule,
      startTime: f.startTime || null,
      hours: hours !== null && Number.isFinite(hours) ? hours : null,
      endsOn: repeating && !keepsGoing && f.endsOn ? f.endsOn : null,
      propertyId: f.propertyId || null,
      crew: [...crew],
      billing: f.billing,
      officeNotes: f.officeNotes.trim() || undefined,
      crewNotes: f.crewNotes.trim() || undefined,
      crewNotesEs: f.crewNotesEs.trim() || undefined,
      checklistId: f.checklistId || null,
    };
    const done = (x: JobDetail) => {
      if (x.confirm)
        toast(
          x.confirm.sentTo
            ? `Job #${String(x.number ?? "")} made. Confirm link sent to ${x.confirm.sentTo}.`
            : `Job #${String(x.number ?? "")} made. Send the confirm link from the job.`,
        );
      else toast(j ? "Job saved" : `Job #${String(x.number ?? "")} made`);
      if (!j && !x.confirm) markFresh(`job:${x.id}`);
      if (q) void qc.invalidateQueries({ queryKey: ["quote"] });
      void navigate({ to: "/jobs/$id", params: { id: x.id } });
    };
    const fail = (er: unknown) => setError(errText(er));
    if (j)
      update.mutate(
        {
          id: j.id,
          patch: {
            ...body,
            ...(switching && nextOn ? { repeatFrom: nextOn } : {}),
            ...(linesTouched ? { lines: out, linesFrom: repeating ? linesFrom : (j.startDate ?? today) } : {}),
          },
        },
        { onSuccess: done, onError: fail },
      );
    else
      create.mutate(
        { ...body, ...(client ? { clientId: client } : {}), ...(q ? { quoteId: q.id } : {}), lines: out, askConfirm },
        { onSuccess: done, onError: fail },
      );
  };
  const pickedRow = (clients.data?.items ?? []).find((c) => c.id === client);
  return (
    <Page
      title={j ? `Edit job #${String(j.number ?? "")}` : "New job"}
      subtitle={j?.clientName ?? (q ? `From quote #${String(q.number)}` : undefined)}
    >
      <form className="stack pform" onSubmit={submit}>
        {error ? <Banner type="error">{error}</Banner> : null}
        {j ? null : (
          <Card title="Client">
            <div className="stack">
              {q ? (
                <div>
                  <b>{who?.name ?? q.contactName ?? q.name}</b>
                  <p className="muted small">
                    {q.clientId
                      ? [q.address ?? who?.address, phoneText(who?.phone ?? q.contactPhone)].filter(Boolean).join(" · ")
                      : `${[q.address, phoneText(q.contactPhone)].filter(Boolean).join(" · ")}${q.address || q.contactPhone ? ". " : ""}They become a client when you make the job.`}
                  </p>
                </div>
              ) : (
                <Combobox
                  label="Client"
                  value={client}
                  onChange={(v) => {
                    if (v === NEW_CLIENT) return;
                    setClient(v);
                    setConfirm(null);
                    setF({ ...f, propertyId: "" });
                  }}
                  options={(clients.data?.items ?? []).map((c) => ({
                    value: c.id,
                    label: c.name,
                    sub: [c.address, [c.email, phoneText(c.phone)].filter(Boolean).join(" · ")].filter(Boolean).join("\n"),
                  }))}
                  last={{
                    value: NEW_CLIENT,
                    label: "+ Create new client",
                    onPick: (typed) => {
                      const [first = "", ...rest] = typed.split(/\s+/);
                      setNewClient({ firstName: first, lastName: rest.join(" ") });
                    },
                  }}
                />
              )}
              {(who?.properties.length ?? 0) > 1 ? (
                <Combobox
                  label="Where"
                  value={f.propertyId}
                  onChange={(v) => setF({ ...f, propertyId: v })}
                  options={(who?.properties ?? []).map((p) => ({
                    value: p.id,
                    label: p.label ? `${p.label} · ${p.address}` : p.address,
                  }))}
                />
              ) : !q && (who || pickedRow) ? (
                <p className="muted small">
                  {[pickedRow?.address ?? who?.address, phoneText(pickedRow?.phone ?? who?.phone ?? null)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}
            </div>
          </Card>
        )}
        {j || q ? null : company.trade === "cleaning" ? (
          <QuickPrice
            size={size}
            useLabel="Put it on the job"
            planStart={who?.planStart}
            onUse={(q, a) => applyQuick(q, a)}
            onHourly={(q) => applyQuick(q, null)}
          />
        ) : company.trade === "lawn" ? (
          <LawnQuickPrice
            sqft={place?.lawnSqft}
            address={place?.address ?? who?.address ?? null}
            useLabel="Put it on the job"
            onUse={(q) => applyQuick(q, null)}
          />
        ) : null}
        <Card title="The work">
          <div className="stack">
            <div className="form-grid">
              <InputText
                label="Job title"
                placeholder="Weekly House Cleaning"
                description="Leave it blank to use the service."
                {...set("title")}
              />
              <InputText label="Service" placeholder="House Cleaning" {...set("service")} />
            </div>
            <RepeatPicker value={repeat} date={repeatDate} onChange={pickRepeat} />
            {switching && nextOn ? (
              <InputText
                label="Next visit"
                type="date"
                value={nextOn}
                min={today}
                onChange={(e) => {
                  const d = e.target.value;
                  if (!d) return;
                  setNextOn(d);
                  setRepeat(moveRepeat(repeat, d));
                }}
                description={
                  j?.lastDone
                    ? `Counted from the last visit on ${shortDate(j.lastDone)}. Visits before today stay where they are.`
                    : "No visit done yet, so it goes on today. Visits before today stay where they are."
                }
              />
            ) : null}
            <LinesEditor
              lines={lines}
              repeats={repeating}
              onChange={(l) => {
                setLines(l);
                setLinesTouched(true);
              }}
            />
            {j && repeating && linesTouched ? (
              <InputText
                label="New prices start on"
                type="date"
                value={linesFrom}
                min={today}
                onChange={(e) => setLinesFrom(e.target.value)}
                description="Visits before this keep the old prices."
              />
            ) : null}
            <Choice
              label="Billing"
              value={f.billing}
              onChange={(v) => setF({ ...f, billing: v })}
              options={[
                { value: "visit", label: "After each visit" },
                { value: "monthly", label: "Once a month" },
                { value: "monthend", label: "At month end" },
                { value: "outside", label: "Not billed here" },
              ]}
            />
          </div>
        </Card>
        <Card title="When">
          <div className="stack">
            <div className="form-grid">
              <InputText label={repeating ? "First visit" : "Date"} type="date" {...set("startDate")} />
              <InputText label="Start time" type="time" {...set("startTime")} />
              <InputText label="How long" suffix="hours" inputMode="decimal" placeholder="3" {...set("hours")} />
            </div>
            {repeating && repeat.frequency !== "as-needed" ? (
              <div className="form-grid">
                <Choice
                  label="Ends"
                  value={keepsGoing ? "never" : "on"}
                  onChange={(v) => setKeepsGoing(v === "never")}
                  options={[
                    { value: "never", label: "Keeps going", hint: "No end date" },
                    { value: "on", label: "Ends on a day" },
                  ]}
                />
                {keepsGoing ? null : <InputText label="Ends on" type="date" min={f.startDate} {...set("endsOn")} />}
              </div>
            ) : null}
          </div>
        </Card>
        <Card title="Crew">
          <div className="stack">
            <FormField label="Who goes" description={crew.size ? `${String(crew.size)} picked` : "You can pick the crew later."}>
              <div className="pick-list">
                {team.map((p) => (
                  <label key={p.id} className="pick">
                    <input
                      type="checkbox"
                      checked={crew.has(p.id)}
                      onChange={() => {
                        const n = new Set(crew);
                        if (n.has(p.id)) n.delete(p.id);
                        else n.add(p.id);
                        setCrew(n);
                      }}
                    />
                    <b>{p.name}</b>
                    <span className="muted">{p.role === "crew" ? "" : p.role}</span>
                  </label>
                ))}
              </div>
            </FormField>
            {checklists.data?.length ? (
              <Combobox
                label="Checklist"
                value={f.checklistId}
                onChange={(v) => setF({ ...f, checklistId: v })}
                options={[{ value: "", label: "No checklist" }, ...checklists.data.map((c) => ({ value: c.id, label: c.name }))]}
              />
            ) : null}
            <FormField label="Notes for the crew">
              <textarea
                rows={3}
                value={f.crewNotes}
                onChange={(e) => setF({ ...f, crewNotes: e.target.value })}
                placeholder="Dog is friendly. Start upstairs."
              />
            </FormField>
            <FormField label="Notes for the crew in Spanish" description="Crew who use the app in Spanish see these.">
              <textarea
                rows={3}
                value={f.crewNotesEs}
                onChange={(e) => setF({ ...f, crewNotesEs: e.target.value })}
                placeholder="El perro es amigable."
              />
            </FormField>
          </div>
        </Card>
        <Card title="Office">
          <FormField label="Office note" description="The crew does not see it.">
            <textarea rows={2} value={f.officeNotes} onChange={(e) => setF({ ...f, officeNotes: e.target.value })} />
          </FormField>
        </Card>
        {!j && who ? (
          <Card title="Confirm with the client">
            <label className="row">
              <Switch checked={askConfirm} onChange={setConfirm} label="Client confirms and signs" />
              <span>
                <b>Client confirms and signs</b>
                <span className="muted small" style={{ display: "block" }}>
                  {askConfirm
                    ? `We text ${who.firstName ?? who.name} a link to confirm the place and day. ${hasCard ? "They sign. Their card is on file." : "They save a card and sign."} The job goes on the schedule now, marked Not confirmed until they do.`
                    : signed
                      ? "They already signed a quote. The job goes on the schedule now and they get the usual booked text."
                      : "The job goes on the schedule now. No confirm link."}
                </span>
              </span>
            </label>
          </Card>
        ) : null}
        <div className="row end form-bar">
          <Button type="tertiary" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <Button submit loading={create.isPending || update.isPending}>
            {j ? "Save job" : askConfirm ? "Make job and send link" : "Make job"}
          </Button>
        </div>
        {!j && repeating ? (
          <p className="muted small">
            <Icon name="repeat" size={14} /> Visits fill the schedule from the first visit on. You can move or skip any of them.
          </p>
        ) : null}
      </form>
      <NewClientBox
        start={newClient}
        trade={company.trade}
        onClose={() => setNewClient(null)}
        onMade={(c) => {
          setNewClient(null);
          setClient(c.id);
          setMadeHere(c.id);
          setConfirm(null);
          setF({ ...f, propertyId: "" });
          setError(null);
        }}
      />
    </Page>
  );
}
